import React, { useState, useEffect } from "react";
import Routes from "./routes";
import "react-toastify/dist/ReactToastify.css";

import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { ptBR } from "@material-ui/core/locale";
import {
  getApplicationErrorCopy,
  getErrorText,
  isDomMutationError,
  isTransientLoadError,
  reloadOnce
} from "./utils/appErrorRecovery";

const reloadOnceAfterDomMutation = error => {
  if (!isDomMutationError(error)) {
    return false;
  }

  return reloadOnce("flowch_dom_mutation_reload");
};

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorText: "", transient: false };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      errorText: getErrorText(error),
      transient: isTransientLoadError(error)
    };
  }

  componentDidCatch(error) {
    if (reloadOnceAfterDomMutation(error)) {
      return;
    }

    if (isTransientLoadError(error) && reloadOnce("flowch_transient_load_reload", 1200)) {
      return;
    }

    this.setState({
      hasError: true,
      errorText: getErrorText(error),
      transient: isTransientLoadError(error)
    });
  }

  render() {
    if (this.state.hasError) {
      const copy = getApplicationErrorCopy();
      return (
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#f3f4f6",
            fontFamily: "Arial, sans-serif",
            color: "#1f2937",
            padding: 24
          }}
        >
          <div
            style={{
              width: "min(560px, 100%)",
              background: "#ffffff",
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              boxShadow: "0 16px 40px rgba(15, 23, 42, 0.12)",
              padding: 28
            }}
          >
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                background: "#ede9fe",
                color: "#6e2ad3",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 24,
                fontWeight: 700,
                marginBottom: 18
              }}
            >
              !
            </div>
            <h1 style={{ fontSize: 22, lineHeight: 1.25, margin: "0 0 10px" }}>
              {copy.title}
            </h1>
            <p style={{ fontSize: 15, lineHeight: 1.55, color: "#4b5563", margin: "0 0 18px" }}>
              {this.state.transient ? copy.transient : copy.description}
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                height: 42,
                border: 0,
                borderRadius: 6,
                background: "#6e2ad3",
                color: "#ffffff",
                fontWeight: 700,
                padding: "0 18px",
                cursor: "pointer"
              }}
            >
              {copy.refresh}
            </button>
            <details style={{ marginTop: 20 }}>
              <summary style={{ cursor: "pointer", color: "#6e2ad3", fontWeight: 700 }}>
                {copy.details}
              </summary>
              <pre
                style={{
                  whiteSpace: "pre-wrap",
                  maxHeight: 220,
                  overflow: "auto",
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: 6,
                  padding: 12,
                  color: "#374151"
                }}
              >
                {this.state.errorText}
              </pre>
            </details>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const App = () => {
  const [locale, setLocale] = useState();

  const theme = createTheme(
    {
      scrollbarStyles: {
        "&::-webkit-scrollbar": {
          width: "8px",
          height: "8px",
        },
        "&::-webkit-scrollbar-thumb": {
          boxShadow: "inset 0 0 6px rgba(0, 0, 0, 0.3)",
          backgroundColor: "#e8e8e8",
        },
      },
      palette: {
        primary: { main: "#6E2AD3" },
      },
    },
    locale
  );

  useEffect(() => {
    let i18nlocale = "";

    try {
      i18nlocale = String(localStorage.getItem("i18nextLng") || "");
    } catch (_err) {
      i18nlocale = "";
    }

    const normalizedLocale = i18nlocale.replace("-", "_");
    if (normalizedLocale.toLowerCase() === "pt_br") {
      setLocale(ptBR);
    }

    const cleanupTimer = window.setTimeout(() => {
      try {
        sessionStorage.removeItem("flowch_dom_mutation_reload");
        sessionStorage.removeItem("flowch_transient_load_reload");
      } catch (_err) {
        // Ignore storage restrictions.
      }
    }, 3000);

    return () => window.clearTimeout(cleanupTimer);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <AppErrorBoundary>
        <Routes />
      </AppErrorBoundary>
    </ThemeProvider>
  );
};

export default App;
